<template>
    <div class="deploymentMarkerContainer" :class="classes" @mousedown.stop>
        <div class="deploymentMarker" @click="$emit('click:marker', $event)">
            <slot></slot>
            <template v-if="isAddButton">+</template>
            <div class="deploymentMarkerCorner"></div>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {ShiftTemplateColor} from '@cquential/api';

    @Component({
        emits: ['click:marker'],
    })
    export default class MarkerContainer extends Vue {
        @Prop({type: Boolean}) public showWarning!: boolean;
        @Prop({type: Boolean}) public busy!: boolean;
        @Prop({type: Boolean}) public fromOtherRoster!: boolean;
        @Prop({type: Boolean}) public toOtherRoster!: boolean;
        @Prop({type: Boolean}) public readonly!: boolean;
        @Prop({type: Boolean}) public availabilityConflicted!: boolean;
        @Prop({type: Boolean}) public isAddButton!: boolean;
        @Prop({type: Boolean}) public isProposed!: boolean;
        @Prop({type: Boolean}) public hasComment!: boolean;
        @Prop({type: Boolean}) public isNormalState!: boolean;
        @Prop({type: Boolean}) public isSick!: boolean;
        @Prop({type: Boolean}) public isStamp!: boolean;
        @Prop() public markingColorId?: ShiftTemplateColor;

        public get classes(): Record<string, boolean> {
            return {
                showWarning: this.showWarning,
                busy: this.busy,
                fromOtherRoster: this.fromOtherRoster,
                toOtherRoster: this.toOtherRoster,
                readonly: this.readonly,
                availabilityConflicted: this.availabilityConflicted,
                isAddButton: this.isAddButton,
                isProposed: this.isProposed,
                hasComment: this.hasComment,
                isNormalState: this.isNormalState,
                isSick: this.isSick,
                isStamp: this.isStamp,
                isEveningShift: this.markingColorId === ShiftTemplateColor.Evening,
                isNightShift: this.markingColorId === ShiftTemplateColor.Night,
                isOtherShift: this.markingColorId === ShiftTemplateColor.Other,
            };
        }
    }
</script>

<style lang="scss" scoped>
    .deploymentMarkerContainer {
        display: inline-block;
        padding-right: var(--deployment-marker-spacing);
        padding-top: var(--deployment-marker-spacing);

        &.isEveningShift, &.isNightShift, &.isOtherShift, &.hasComment {
            // add space for the corner marker
            .deploymentMarker {
                padding-right: 0.625rem;
            }
        }

        &.isEveningShift .deploymentMarkerCorner {
            border-right-color: var(--deployment-marker-evening-color);
        }

        &.isNightShift .deploymentMarkerCorner {
            border-right-color: var(--deployment-marker-night-color);
        }

        &.isOtherShift .deploymentMarkerCorner {
            border-right-color: var(--deployment-marker-other-color);
        }
    }

    .deploymentMarker {
        position: relative;
        background: var(--deployment-marker-default-background);
        box-shadow: var(--deployment-marker-default-shadow);
        border-top: 1px solid var(--deployment-marker-border-top-color);
        box-sizing: border-box;
        cursor: pointer;
        font-weight: bold;
        max-width: var(--marker-max-width, '0px');
        word-break: var(--marker-word-break, 'break-all');
        overflow: var(--marker-overflow, 'visible');
        padding: 0 var(--deployment-marker-padding-right-left);
        font-size: var(--deploymentmarker-font-size);
        border-radius: var(--theme-border-radius-small);
        transition: box-shadow var(--theme-generic-transition);

        &:hover {
            box-shadow: var(--deployment-marker-hover-shadow);
        }
    }

    .deploymentMarkerCorner {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        border-style: solid;
        border-width: 0 var(--deployment-marker-visible-size) var(--deployment-marker-visible-size) 0;
        border-color: transparent;
    }

    .deploymentMarkerContainer.active {
        .deploymentMarker {
            border: 1px solid var(--deployment-marker-active-border-color);
            box-shadow: var(--deployment-marker-active-box-shadow);
            opacity: 1;
        }
    }

    .readonly, .busy {
        touch-action: none;
        pointer-events: none;

        .deploymentMarker {
            background: var(--deployment-marker-inactive-background);
            color: var(--deployment-marker-inactive-color);
            box-shadow: none; // TODO check if needed
        }
    }

    .hasComment {
        .deploymentMarkerCorner {
            border-right-color: var(--deployment-marker-indicator-comment-color);
        }

        &.toOtherRoster {
            .deploymentMarkerCorner {
                border-right-color: var(--deployment-marker-indicator-to-other-roster-comment-color);
            }
        }
    }

    .toOtherRoster {
        .deploymentMarker {
            background: var(--deployment-marker-to-other-roster-background);
            color: var(--deployment-marker-to-other-roster-color);
            border: 1px solid var(--deployment-marker-to-other-roster-border-color);
        }
    }

    .isNormalState.showWarning {
        .deploymentMarker {
            color: var(--deployment-marker-warning-color);
        }
    }

    .fromOtherRoster {
        .deploymentMarker {
            text-shadow: var(--deployment-marker-from-other-roster-shadow);
            background: var(--deployment-marker-from-other-roster-background);
        }
    }

    .availabilityConflicted {
        .deploymentMarker {
            background: var(--deployment-marker-availability-conflicted-background);
            color: var(--deployment-marker-availability-conflicted-color)
        }
    }

    // semi cross-component styling, uses parent selectors
    .is-completely-sick .deploymentMarker:not(.isSick) {
        color: var(--deployment-marker-deployment-during-complete-sickness-color);
    }

    // semi cross-component styling, uses parent selectors
    .is-partly-sick .deploymentMarker:not(.isSick) {
        color: var(--deployment-marker-deployment-during-partial-sickness-color);
    }

    .is-not-employed {
        .toOtherRoster {
            .deploymentMarker {
                background: var(--deployment-marker-outside-employment-background);
                border: 1px solid var(--deployment-marker-outside-employment-border-color);
            }
        }

        .readonly {
            background: var(--deployment-marker-outside-employment-readonly-background);
        }
    }

    .isAddButton {
        .deploymentMarker {
            background-color: var(--deployment-marker-add-button-background);
            text-shadow: var(--deployment-marker-add-button-shadow);
            color: var(--deployment-marker-add-button-color) !important;
            font-size: var(--deploymentmarker-font-size);
            opacity: 0;
            min-width: 1.25rem;
            text-align: center;
        }

        &:only-child {
            width: 100%;

            .deploymentMarker {
                width: 100%;
            }
        }
    }

    .isProposed {
        .deploymentMarker {
            outline: 1px dashed var(--deployment-marker-proposed-deployment-border-color);
        }
    }

    // semi cross-component, uses parent selector
    .cell:hover {
        .isAddButton {
            .deploymentMarker {
                opacity: 1;
            }
        }
    }
</style>
